// CRITICAL: Import the Quill CSS
import "react-quill-new/dist/quill.snow.css";

import { InputContainer, Label } from "./Inputs";
import { colors, fontFamily, fontSize } from "../../assets/styles/parameters";

import ReactQuill from "react-quill-new";
import { Span } from "../typography";
import styled from "styled-components";

export const RichTextField = (props) => {
  // 1. Extract React Final Form props (input, meta) and your custom UI props
  const { input, meta, label, entity, ...otherProps } = props;
  const { touched, valid, error } = meta;

  // 2. React Final Form Event Handlers
  const handleChange = (content) => {
    input.onChange(content); // Syncs the raw HTML to the form state
  };

  const handleBlur = () => {
    input.onBlur(); // CRITICAL: Tells Final Form the user left the field so validation runs
  };

  const handleFocus = () => {
    input.onFocus(); // Tells Final Form the field is currently active
  };

  // 3. Robust, Extensive Toolbar Configuration
  const modules = {
    toolbar: [
      // Font Family and Size Dropdowns
      [{ font: [] }],
      [{ size: ["small", false, "large", "huge"] }],

      // Full Header Support (H1 - H6)
      [{ header: [1, 2, 3, 4, 5, 6, false] }],

      // Core Text Formatting
      ["bold", "italic", "underline", "strike"],

      // Colors (Text Color & Background Highlight)
      [{ color: [] }, { background: [] }],

      // Scripts (Subscript & Superscript)
      [{ script: "sub" }, { script: "super" }],

      // Blocks (Quotes & Code Snippets)
      ["blockquote", "code-block"],

      // Lists & Indentation
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],

      // Text Alignment & Direction (Right-to-Left support)
      [{ align: [] }],
      [{ direction: "rtl" }],

      // Media & Links
      ["link", "image", "video"],

      // Clear Formatting Button
      ["clean"],
    ],
    // Clean up clipboard pasting (prevents weird spacing from MS Word)
    clipboard: {
      matchVisual: false,
    },
  };

  return (
    <InputContainer {...otherProps}>
      {/* Standard App Label with Required Star Support */}
      {label && <Label text={label} entity={entity} />}

      <StyledQuill
        theme="snow"
        value={input.value || ""} // Fallback to "" prevents React uncontrolled input warnings
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        modules={modules}
      />

      {/* React Final Form Validation Error Rendering */}
      {!valid && touched && error ? (
        <Span color="red" size="small">
          {error}
        </Span>
      ) : null}
    </InputContainer>
  );
};

export default RichTextField;

// --- STYLED COMPONENTS ---

const StyledQuill = styled(ReactQuill)`
  /* Ensure the entire editor never escapes its container */
  width: 100%;
  max-width: 100%;

  /* Mimic the BaseInput focus state */
  &:focus-within {
    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
      background-color: ${colors.white};
      border-color: ${colors.black};
    }
  }

  /* Mimic the BaseInput hover state */
  &:hover {
    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
      border-color: ${colors.black};
    }
  }

  /* Toolbar styling to match your app's inputs */
  .ql-toolbar.ql-snow {
    background-color: ${colors.gray200};
    border: 0.5px solid ${colors.gray400};
    border-bottom: none;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    font-family: ${fontFamily.body}, Arial, sans-serif;
    transition: all 0.2s ease-in-out;

    /*
     * The Quill snow toolbar renders all buttons inline and never wraps.
     * On narrow viewports this forces the page wider than the screen.
     * Scrolling the toolbar in-place keeps the page layout contained while
     * still exposing every button to the user.
     */
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;

    .ql-formats {
      display: inline-flex;
      margin-right: 8px;
      vertical-align: middle;
    }
  }

  /* Text Area styling to match your app's inputs */
  .ql-container.ql-snow {
    background-color: ${colors.gray200};
    border: 0.5px solid ${colors.gray400};
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    font-family: ${fontFamily.body}, Arial, sans-serif;
    font-size: ${fontSize.default};
    transition: all 0.2s ease-in-out;
    min-height: 250px;
    overflow-x: hidden;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .ql-editor {
    min-height: 250px;
    max-width: 100%;
    overflow-x: hidden;
    word-break: break-word;
    overflow-wrap: break-word;

    img {
      max-width: 100%;
      height: auto;
    }

    table {
      max-width: 100%;
      width: 100% !important;
      table-layout: fixed;
    }
  }

  /* Compact toolbar buttons slightly on mobile phones */
  @media screen and (max-width: 480px) {
    .ql-toolbar.ql-snow {
      padding: 6px 8px;

      button {
        width: 24px;
        height: 24px;
        padding: 2px;
      }

      .ql-picker {
        height: 24px;
      }
    }
  }
`;
